/**
 * Theme presets — palettes for the Settings → Appearance picker.
 *
 * Mission Control is the default (and its CSS lives directly in
 * `index.css`, so its `light` / `dark` overrides here stay empty).
 * Every other preset ships its full set of overrides — the
 * `useThemePalette` hook applies them as inline custom properties
 * on `<html>` whenever the picker switches.
 *
 * Variable contract per palette (must define both light + dark):
 *   --background, --foreground
 *   --card, --card-foreground
 *   --primary, --primary-foreground
 *   --secondary, --secondary-foreground
 *   --muted, --muted-foreground
 *   --accent, --accent-foreground
 *   --border, --ring
 *
 * Values are HSL channels in the space-separated form shadcn expects
 * (e.g. `268 47% 6%`, NOT `hsl(268, 47%, 6%)`).
 */

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  tagline?: string;
  isPremium: boolean;
  // PLANS-1 — mirrors the paid lineup. `orbit_starter` = every paid
  // plan; `orbit` = Orbit or Business; `orbit_business` = Business only.
  tier: 'orbit_starter' | 'orbit' | 'orbit_business';
  previewColors: string[];
  isSpaceTheme?: boolean;
  light: Record<string, string>;
  dark: Record<string, string>;
}

export const themePresets: ThemePreset[] = [
  {
    id: 'default',
    name: 'Mission Control',
    description: 'Operational configuration',
    tagline: 'Operational. Clear. Built for long sessions.',
    isPremium: false,
    tier: 'orbit_starter',
    // Mission Control palette preview swatches:
    // cosmic ivory · atomic cream · orbit orange · lunar teal · deep space
    previewColors: ['#F4EFE6', '#E8DFC8', '#D4541F', '#2D6E70', '#1C1A17'],
    light: {},
    dark: {},
  },

  // ─── Andromeda ────────────────────────────────────────────────────────
  // Galaxy-inspired. Vibrant magenta replaces the orange hero;
  // electric cyan replaces the lunar teal accent. Light mode is a
  // pale orchid canvas with the feel of an aurora at dawn; dark mode
  // is a deep cosmic void with magenta + cyan punching through.
  {
    id: 'andromeda',
    name: 'Andromeda',
    description: 'Galaxy on the canvas',
    tagline: 'Magenta nebula, electric cyan, deep cosmic ground.',
    isPremium: false,
    tier: 'orbit_starter',
    isSpaceTheme: true,
    // pale orchid · cream lift · nebula magenta · electric cyan · cosmic void
    previewColors: ['#F6F0FB', '#FCF8FE', '#C84CE0', '#14B5C7', '#110917'],
    light: {
      '--background': '285 38% 96%',
      '--foreground': '268 30% 12%',
      '--card': '285 60% 98%',
      '--card-foreground': '268 30% 12%',
      '--primary': '292 75% 56%',
      '--primary-foreground': '285 38% 96%',
      '--secondary': '285 30% 90%',
      '--secondary-foreground': '268 30% 12%',
      '--muted': '285 30% 93%',
      '--muted-foreground': '268 15% 40%',
      '--accent': '188 80% 42%',
      '--accent-foreground': '285 38% 96%',
      '--border': '285 20% 82%',
      '--ring': '292 75% 56%',
      // Sidebar rail — deeper than the canvas so the nav reads as a
      // discrete band, matching Mission Control's rail-on-canvas pattern.
      '--sidebar-background': '285 30% 88%',
      '--sidebar-foreground': '268 30% 12%',
      '--sidebar-primary': '292 75% 56%',
      '--sidebar-primary-foreground': '285 38% 96%',
      '--sidebar-accent': '285 25% 82%',
      '--sidebar-accent-foreground': '268 30% 12%',
      '--sidebar-border': '285 18% 75%',
      '--sidebar-ring': '292 75% 56%',
    },
    dark: {
      '--background': '268 47% 6%',
      '--foreground': '285 35% 90%',
      '--card': '268 35% 11%',
      '--card-foreground': '285 35% 90%',
      '--primary': '292 80% 65%',
      '--primary-foreground': '268 47% 6%',
      '--secondary': '268 30% 18%',
      '--secondary-foreground': '285 35% 90%',
      '--muted': '268 25% 16%',
      '--muted-foreground': '285 22% 70%',
      '--accent': '188 90% 55%',
      '--accent-foreground': '268 47% 6%',
      '--border': '268 30% 22%',
      '--ring': '292 80% 65%',
      '--sidebar-background': '268 45% 8%',
      '--sidebar-foreground': '285 35% 90%',
      '--sidebar-primary': '292 80% 65%',
      '--sidebar-primary-foreground': '268 47% 6%',
      '--sidebar-accent': '268 32% 14%',
      '--sidebar-accent-foreground': '285 35% 90%',
      '--sidebar-border': '268 30% 18%',
      '--sidebar-ring': '292 80% 65%',
    },
  },

  // ─── Earth ────────────────────────────────────────────────────────────
  // Coastal-cliff palette: warm sand canvas, deep ocean-blue primary,
  // warm clay terracotta accent. More blue than green so it reads as
  // ocean-grounded rather than forest. Dark mode is a midnight-tide
  // ocean with ocean-blue and clay glowing on top.
  {
    id: 'earth',
    name: 'Earth',
    description: 'Coastline at golden hour',
    tagline: 'Warm sand, deep ocean, sun-warmed clay.',
    isPremium: false,
    tier: 'orbit_starter',
    isSpaceTheme: true,
    // warm sand · cream lift · deep ocean · warm clay · midnight tide
    previewColors: ['#F2EDE3', '#FAF6EE', '#1B6B87', '#C77B5C', '#101A20'],
    light: {
      '--background': '38 35% 92%',
      '--foreground': '84 15% 14%',
      '--card': '41 56% 96%',
      '--card-foreground': '84 15% 14%',
      '--primary': '196 65% 32%',
      '--primary-foreground': '41 56% 96%',
      '--secondary': '38 30% 86%',
      '--secondary-foreground': '84 15% 14%',
      '--muted': '38 30% 90%',
      '--muted-foreground': '38 10% 38%',
      '--accent': '14 50% 55%',
      '--accent-foreground': '41 56% 96%',
      '--border': '38 22% 76%',
      '--ring': '196 65% 32%',
      // Sidebar rail — deeper warm sand so the nav reads as a discrete band.
      '--sidebar-background': '38 30% 86%',
      '--sidebar-foreground': '84 15% 14%',
      '--sidebar-primary': '196 65% 32%',
      '--sidebar-primary-foreground': '41 56% 96%',
      '--sidebar-accent': '38 25% 80%',
      '--sidebar-accent-foreground': '84 15% 14%',
      '--sidebar-border': '38 20% 72%',
      '--sidebar-ring': '196 65% 32%',
    },
    dark: {
      '--background': '199 30% 9%',
      '--foreground': '43 30% 87%',
      '--card': '199 28% 14%',
      '--card-foreground': '43 30% 87%',
      '--primary': '196 65% 58%',
      '--primary-foreground': '199 30% 9%',
      '--secondary': '199 25% 19%',
      '--secondary-foreground': '43 30% 87%',
      '--muted': '199 22% 17%',
      '--muted-foreground': '43 20% 70%',
      '--accent': '14 55% 65%',
      '--accent-foreground': '199 30% 9%',
      '--border': '199 28% 22%',
      '--ring': '196 65% 58%',
      '--sidebar-background': '199 32% 7%',
      '--sidebar-foreground': '43 30% 87%',
      '--sidebar-primary': '196 65% 58%',
      '--sidebar-primary-foreground': '199 30% 9%',
      '--sidebar-accent': '199 28% 15%',
      '--sidebar-accent-foreground': '43 30% 87%',
      '--sidebar-border': '199 28% 18%',
      '--sidebar-ring': '196 65% 58%',
    },
  },
];

export const getThemeById = (id: string): ThemePreset | undefined => {
  return themePresets.find((theme) => theme.id === id);
};

export const getPremiumThemes = (): ThemePreset[] => {
  return themePresets.filter((theme) => theme.isPremium);
};

export const getThemesByTier = (tier: 'orbit_starter' | 'orbit' | 'orbit_business'): ThemePreset[] => {
  if (tier === 'orbit_business') return themePresets;
  if (tier === 'orbit') return themePresets.filter(t => t.tier === 'orbit_starter' || t.tier === 'orbit');
  return themePresets.filter(t => t.tier === 'orbit_starter');
};
